import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import { Button } from "@/components/ui/button";
import { Check, X, Minus, ArrowRight } from "lucide-react";
import DemoScheduler from "@/components/DemoScheduler";

const Comparison = () => {
    const [demoOpen, setDemoOpen] = useState(false);

    const features = [
        {
            name: "Arquitetura",
            aroan: "AI-Native (IA no centro)",
            legacy: "Bolt-On (IA adicionada depois)",
            generic: "Genérica (Sem contexto)",
        },
        {
            name: "Tempo de Implementação",
            aroan: "4-6 Semanas",
            legacy: "6-18 Meses",
            generic: "Varia (Requer dev)",
        },
        {
            name: "Custo Total (TCO)",
            aroan: "Baixo (All-in-one)",
            legacy: "Alto (Licenças + Consultoria)",
            generic: "Médio (API + Dev)",
        },
        {
            name: "Unificação de Canais",
            aroan: true,
            legacy: "Parcial (Módulos extras)",
            generic: false,
        },
        {
            name: "Automação No-Code",
            aroan: true,
            legacy: "Complexo (Low-code)",
            generic: false,
        },
        {
            name: "Contexto Empresarial",
            aroan: "Nativo (VIA)",
            legacy: "Limitado",
            generic: "Nenhum",
        },
    ];

    return (
        <div className="min-h-screen bg-background">
            <SEO
                title="Aroan vs ITSM Tradicional e IAs Genéricas: Comparativo"
                description="Compare a Aroan com ferramentas tradicionais de ITSM e IAs genéricas: tempo de implementação, automações no-code, IA treinada no seu contexto, custo total e suporte."
                canonical="https://aroan.com.br/comparar"
                ogType="website"
                article={false}
            />
            <BreadcrumbSchema
                items={[
                    { name: "Home", url: "https://aroan.com.br/" },
                    { name: "Comparar", url: "https://aroan.com.br/comparar" },
                ]}
            />
            <Header />

            {/* Hero Section */}
            <section className="pt-32 pb-20 bg-gradient-hero">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 animate-fade-in">
                        Por Que Escolher <span className="text-primary">AROAN</span>?
                    </h1>
                    <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8 animate-fade-in-delay-1">
                        Veja como nossa abordagem AI-Native se compara às soluções tradicionais de ITSM e ferramentas genéricas de IA.
                    </p>
                </div>
            </section>

            {/* Comparison Table */}
            <section className="py-20 bg-background">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse min-w-[800px]">
                            <thead>
                                <tr>
                                    <th className="p-6 text-left text-xl font-semibold border-b border-border w-1/4">Recurso / Critério</th>
                                    <th className="p-6 text-center text-xl font-bold text-primary border-b border-primary bg-primary/5 rounded-t-xl w-1/4">
                                        AROAN
                                    </th>
                                    <th className="p-6 text-center text-xl font-semibold text-muted-foreground border-b border-border w-1/4">
                                        Legacy ITSM
                                        <span className="block text-sm font-normal mt-1">(ServiceNow, Zendesk)</span>
                                    </th>
                                    <th className="p-6 text-center text-xl font-semibold text-muted-foreground border-b border-border w-1/4">
                                        IA Genérica
                                        <span className="block text-sm font-normal mt-1">(ChatGPT, Copilot)</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {features.map((feature, index) => (
                                    <tr key={index} className="hover:bg-secondary/20 transition-colors">
                                        <td className="p-6 border-b border-border font-medium text-foreground">
                                            {feature.name}
                                        </td>
                                        <td className="p-6 border-b border-primary/20 bg-primary/5 text-center font-semibold text-foreground">
                                            {typeof feature.aroan === "boolean" ? (
                                                feature.aroan ? <Check className="w-6 h-6 text-primary mx-auto" /> : <X className="w-6 h-6 text-destructive mx-auto" />
                                            ) : (
                                                feature.aroan
                                            )}
                                        </td>
                                        <td className="p-6 border-b border-border text-center text-muted-foreground">
                                            {typeof feature.legacy === "boolean" ? (
                                                feature.legacy ? <Check className="w-6 h-6 text-green-500 mx-auto" /> : <X className="w-6 h-6 text-red-400 mx-auto" />
                                            ) : (
                                                feature.legacy === "Parcial (Módulos extras)" ? (
                                                    <span>Parcial <span className="text-xs block">(Módulos extras)</span></span>
                                                ) : feature.legacy
                                            )}
                                        </td>
                                        <td className="p-6 border-b border-border text-center text-muted-foreground">
                                            {typeof feature.generic === "boolean" ? (
                                                feature.generic ? <Check className="w-6 h-6 text-green-500 mx-auto" /> : <X className="w-6 h-6 text-red-400 mx-auto" />
                                            ) : (
                                                feature.generic
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* Deep Dive Sections */}
            <section className="py-20 bg-secondary/30">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <div>
                            <h2 className="text-3xl font-bold mb-6">O Problema do "Bolt-On AI"</h2>
                            <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                                Ferramentas tradicionais foram construídas há décadas. Para tentar acompanhar a inovação, elas "colam" funcionalidades de IA por cima de sistemas legados.
                            </p>
                            <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                                O resultado? Uma experiência fragmentada, dados desconectados e alucinações frequentes, pois a IA não tem acesso profundo ao contexto da sua operação.
                            </p>
                        </div>
                        <div className="bg-background p-8 rounded-2xl border border-border shadow-sm">
                            <h3 className="text-xl font-bold mb-4 text-destructive">Abordagem Tradicional</h3>
                            <ul className="space-y-3">
                                <li className="flex items-start gap-3">
                                    <X className="w-5 h-5 text-destructive mt-1 shrink-0" />
                                    <span className="text-muted-foreground">IA é um "add-on" pago à parte</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <X className="w-5 h-5 text-destructive mt-1 shrink-0" />
                                    <span className="text-muted-foreground">Dados isolados em silos</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <X className="w-5 h-5 text-destructive mt-1 shrink-0" />
                                    <span className="text-muted-foreground">Meses para configurar e treinar</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-20 bg-background">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <div className="order-2 md:order-1 bg-primary/5 p-8 rounded-2xl border border-primary/20 shadow-sm">
                            <h3 className="text-xl font-bold mb-4 text-primary">A Diferença AROAN</h3>
                            <ul className="space-y-3">
                                <li className="flex items-start gap-3">
                                    <Check className="w-5 h-5 text-primary mt-1 shrink-0" />
                                    <span className="text-foreground">IA no núcleo da plataforma (AI-Native)</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <Check className="w-5 h-5 text-primary mt-1 shrink-0" />
                                    <span className="text-foreground">Contexto unificado de todos os canais</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <Check className="w-5 h-5 text-primary mt-1 shrink-0" />
                                    <span className="text-foreground">Pronto para usar em semanas, não meses</span>
                                </li>
                            </ul>
                        </div>
                        <div className="order-1 md:order-2">
                            <h2 className="text-3xl font-bold mb-6">A Vantagem AI-Native</h2>
                            <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                                AROAN foi construída na era da IA, para a era da IA. Nossa arquitetura unifica dados, processos e pessoas em uma única camada inteligente.
                            </p>
                            <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                                Isso permite que nossa IA (VIA) entenda o contexto completo de cada solicitação, automatizando com precisão e aprendendo continuamente com sua operação.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 bg-gradient-hero text-center">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                        Pronto para Modernizar sua Operação?
                    </h2>
                    <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                        Deixe o legado para trás. Experimente o poder de uma plataforma verdadeiramente inteligente.
                    </p>
                    <Button size="lg" className="group" onClick={() => setDemoOpen(true)}>
                        Agende uma Demo Comparativa
                        <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                </div>
            </section>

            <Footer />
            <DemoScheduler open={demoOpen} onOpenChange={setDemoOpen} />
        </div>
    );
};

export default Comparison;
